import React, { useState, useEffect, useMemo, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import PropTypes from 'prop-types';

import {
  MenuItem,
  Tooltip,
  TextField,
  Typography
} from '@material-ui/core';

import {useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import DateFnsUtils from '@date-io/date-fns';
import esLocale from 'date-fns/locale/es'

import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';

import { useExpanded, useTable } from "react-table";

import { NavTopMenu } from '../shared/NavTopMenu';
import { Footer } from '../shared/Footer';
import { SidebarMenu } from '../shared/SidebarMenu';

import '../../styles/style.scss';
import '../../styles/saleOrderStyle.scss';

import advanceAndInvoiceService from "../../services/advance-and-invoice.service";

import { convertDateToJulian } from '../../constants/component/convertDateToJulian';
import { sesionExpired } from '../../constants/auth/sesionExpired';
import { timeSessionDifference } from '../../constants/auth/timeSessionDifference';
import { actionErrorConnection } from "../../constants/component/actionErrorConnection";
import usersService from '../../services/users.service';


export const AdvanceAndInvoiceScreen = () => {

  const currencySelected = 'es-Mx';

  const { isLoggedIn, user } = useSelector(state => state.auth);
  const an8Logged = isLoggedIn ? user.noCustomer : '---';

  const dateMin = new Date();

  const [selectStartDate, handleStartDateChange] = useState(null);
  const [selectEndDate, handleEndDateChange] = useState(null);
  const [isDownloadFile, setIsDownloadFile] = useState(false)
  const dispatch = useDispatch();
  const history = useHistory();
  const tokenExpired = process.env.REACT_APP_TIME_EXPIRED_TOKEN;
  const sessionDifference = timeSessionDifference();

  const [customers, setCustomers] = useState({
    isLoading: true,
    isDisabled: true,
    data: []
  });

  const [saleOrder, setSaleOrder] = useState([]);
  const [saleOrderFile, setSaleOrderFile] = useState([])
  const saleOrderRef = useRef();

  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isLoadingDetailData, setIsLoadingDetailData] = useState(false);
  const [isSubmitingData, setIsSubmitingData] = useState(false);
  const [errorResponse, setErrorResponse] = useState('');
  const [isErrorResponse, setIsErrorResponse] = useState(false);

  saleOrderRef.current = saleOrder;

  const validationSchema = Yup.object().shape({
    an8: Yup.string().required('Cliente es requerido'),
    startDate: Yup.date().default(selectStartDate).required('valor requerido'),
    endDate: Yup.date().default(selectEndDate).required('valor requerido')
      .min(selectStartDate ? selectStartDate : dateMin)
  });

  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    handleStartDateChange(new Date());
    handleEndDateChange(new Date());
    getCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Recupera la lista de clientes
   * @param {string} an8 - identificador del cliente
   * @author alSanchez - ETE
   * @version 1.0.0 - Enero 2024
   */
  const getCustomers = () => {

    setCustomers({
      isLoading: true,
      isDisabled: true,
      data: []
    })
    usersService.getCustomerAssociatesByAN8(an8Logged).then((response) => {
      let customersResponse = response;

      setCustomers({
        isLoading: false,
        isDisabled: false,
        data: customersResponse
      });
    }).catch((error) => {
      console.log(error);
      setCustomers({
        isLoading: false,
        isDisabled: false,
        data: ['']
      });

      if (sessionDifference > Number(tokenExpired)) {
        sesionExpired(dispatch, history);
      } else {
        actionErrorConnection(error, 'Error al recuperar clientes.');
      }
    });
  }

  /**
   *
   * @param {string} an8 - objeto del formulario
   * @param {Date} maxDate - Fecha maxima de busqueda
   * @param {Date} minDate - Fecha minima de busqueda
   * @author alSanchez - ETE
   * @version 1.0.0 - Febrero 2024
   */

  const onSubmit = (data, maxDate, minDate) => {

    const element = document.getElementById('collapseExample');
    element.classList.remove('show');

    setSaleOrder([]);
    setIsLoadingData(true);
    setIsErrorResponse(false);
    setIsSubmitingData(true);

    const params = getValues();
    params.invoiceNumber = '';
    params.invoiceType = '';
    params.origin = '';

      if (!data) {
        params.dateStart = convertDateToJulian(minDate);
        params.dateEnd = convertDateToJulian(maxDate);
      } else {
        params.dateStart = convertDateToJulian(data.startDate);
        params.dateEnd = convertDateToJulian(data.endDate);
      }


    getSaleOrderByFilterService(params);

  };

  /**
   * Recupera la lista de clientes
   * @param {object} dataFilter - objeto con parametros de busqueda
   * @author alSanchez - ETE
   * @version 1.0.0 - Febrero 2024
   */

  const getSaleOrderByFilterService = (dataFilter) => {
    advanceAndInvoiceService.getAdvanceAndInvoicesByFilters(dataFilter)
      .then((response) => {
        if(response.message?.length > 1){
          setIsErrorResponse(true);
          setErrorResponse(response.message);
          setSaleOrder([]);
        }else{
          setIsErrorResponse(false);
          const newResponseData = [];
          response.data.managementOfAdvancesInvoicesResponseList?.forEach((data)=>{
            newResponseData.push({...data, totalRemainderWithVat:response.data.totalRemainderWithVat});
          });
        setSaleOrder(newResponseData);
        setSaleOrderFile(response.data)
        }
        setIsLoadingData(false);
        setIsSubmitingData(true);
      })
      .catch((error) => {
        setIsSubmitingData(true);
        setIsLoadingData(false);
        if (error.response) {
          setErrorResponse(error.response.data.mensaje);
          if (sessionDifference > Number(tokenExpired)) {
            sesionExpired(dispatch, history);
          }
        } else {
          setErrorResponse(error);
        }
        setIsErrorResponse(true);
        actionErrorConnection(error, 'Error al recuperar información.');
      });

  }

  const columns = useMemo(
    () => [
      {
        Header: () => null,
        id: 'expander',
        Cell: ({ row, rows, toggleRowExpanded }) => {
          // const saleOrderDataDetail = saleOrder[row.id];

          const getResponse=()=>{
            row.toggleRowExpanded();
            if(!row.isExpanded){
              console.log('detail')
            }else {
              console.log('no-detail')
            }
          }
          return (
            <Tooltip title={row.isExpanded ? 'Ocultar detalle' : 'Ver detalle'} placement="top-start">
              <div>
                <span {...row.getToggleRowExpandedProps(
                  {
                    onClick: () => {
                      const expandedRow = rows.find(row => row.isExpanded);

                      if (expandedRow) {
                        const isSubItemOfRow = Boolean(
                          expandedRow && row.id.split(".")[0] === expandedRow.id
                        );

                        if (isSubItemOfRow) {
                          const expandedSubItem = expandedRow.subRows.find(
                            subRow => subRow.isExpanded
                          );

                          if (expandedSubItem) {
                            const isClickedOnExpandedSubItem =
                              expandedSubItem.id === row.id;
                            if (!isClickedOnExpandedSubItem) {
                              toggleRowExpanded(expandedSubItem.id, false);
                            }
                          }
                        } else {
                          toggleRowExpanded(expandedRow.id, false);
                        }
                      }
                      getResponse();
                    }
                  }
                )} className="h4">
                  {row.isExpanded ? <i className="fas fa-angle-down custom-btn-icons"></i> : <i className="fas fa-angle-right custom-btn-icons"></i>}
                </span>
              </div>
          </Tooltip>
          )
        },
        enableResizing: false,
        minWidth: 10,
        width: 10,
        maxWidth: 10
      },
      {
        Header: 'Cliente',
        accessor: 'an8',
        enableResizing: false,
        minWidth: 50,
        width: 50,
        maxWidth: 50
      },
      {
        Header: "Fecha Factura",
        accessor: "dateInvoice",
        enableResizing: false,
        minWidth: 100,
        width: 100,
        maxWidth: 100
      },
      {
        Header: "Referencia",
        accessor: "referenceKey",
        enableResizing: false,
        minWidth: 100,
        width: 100,
        maxWidth: 100
      },
      {
        Header: "Num. factura",
        accessor: "numberInvoice",
        enableResizing: false,
        minWidth: 100,
        width: 100,
        maxWidth: 100
      },
      {
        Header: 'Tipo factura',
        accessor: 'typeInvoice',
        enableResizing: false,
        minWidth: 100,
        width: 100,
        maxWidth: 100
      },
      {
        Header: 'Importe sin IVA',
        accessor: 'importWithoutVat',
        enableResizing: false,
        minWidth: 100,
        width: 100,
        maxWidth: 100
      },
      {
        Header: "IVA",
        accessor: "vat",
        enableResizing: false,
        minWidth: 100,
        width: 100,
        maxWidth: 100
      },
      {
        Header: 'Importe con IVA',
        accessor: 'importWithVat',
        enableResizing: false,
        minWidth: 100,
        width: 100,
        maxWidth: 100
      },
      {
        Header: "Remanente con IVA",
        accessor: "remainderWithVat",
        enableResizing: false,
        minWidth: 130,
        width: 130,
        maxWidth: 130
      },
      {
        Header: "Origen",
        accessor: "origin",
        enableResizing: false,
        minWidth: 100,
        width: 100,
        maxWidth: 100
      },
      {
        Header: "Descripción",
        accessor: "description",
        enableResizing: false,
        minWidth: 100,
        width: 100,
        maxWidth: 100
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns
  } = useTable({
    columns,
    data: saleOrder,
  },
  useExpanded);
  const formatDateFile = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear()
    const month =String(date.getMonth() + 1).padStart(2, '0'); 
    const formattedDate = `${year}-${month}`;

    return formattedDate;

  }
  const downloadFile =  async() =>{
    setIsDownloadFile(true)
    const params = getValues()
    const an8 = params.an8
    const startDate = formatDateFile(selectStartDate)
    const endDate = formatDateFile(selectEndDate)
    const fileName = `consulta_de_anticipos_${an8}_${startDate}-${endDate}`;
    advanceAndInvoiceService.downloadAdvanceandInvoicesdataXlsx(saleOrderFile,an8 ).then((response)=>{
      const byteString = window.atob(response.split(',')[1]);
      const mimeString = response.split(',')[0].split(':')[1].split(';')[0];

      const ab = new ArrayBuffer(byteString.length); // se crea un arrayBuffer contiene datos binarios con la misma longitud que la respuesta stringByte
      const ia = new Uint8Array(ab); // para modificar la estructura se inserta en unUndefined 8int Array e
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i) // cada posicion de ia[i] se le asigna la codificacion del caracter en la posicion
      }

      const dataBlob = new Blob([ab], { type: mimeString });

      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(dataBlob, `${fileName}.xls`);
        setIsDownloadFile(false);
        return
      }

      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url
      link.setAttribute('download', `${fileName}.xls`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setIsDownloadFile(false);
    },
    (error)=>{
      setIsDownloadFile(false);
      actionErrorConnection(error, 'Error al descargar archivo xls.');
    });

    setIsLoadingDetailData(false);
    } 
    

  return (
    <>
      <div className="container-fluid p-0 m-0">
        <div className="row flex-nowrap p-0 m-0">
          <div className="col-auto col-md-3 col-xl-2 p-0 d-none d-sm-block">
            <SidebarMenu />
          </div>
          <div className="col p-0 ms-1 me-1 order-content">
            <NavTopMenu />
            <div className="container ps-3 pe-3 container-size">
              <div className="row base-container ">
                <div>
                  <div className="col-12 border-title text-title pt-2 pb-2 ps-4">
                    <span className="h4">Consulta de anticipos</span>
                  </div>
                  <div className="col-12 pt-3 pb-3 ps-1 pe-1">
                    <div className="row ">
                      <div className="col-12 ">
                        <div className="w-100 d-flex justify-content-end">
                          {(customers.isLoading) &&
                            <Tooltip title="Cargando filtro..." placement="top-start" arrow>
                              <div className="custom-icon-loading me-1">
                                <i className="fas fa-spinner fa-spin"></i>
                              </div>
                            </Tooltip>
                          }
                          {
                            isDownloadFile ?
                              <Tooltip title="Descargargando..." placement="top-start" arrow>
                                <div className="custom-icon-loading me-1">
                                  <i className="fas fa-spinner fa-spin"></i>
                                </div>
                              </Tooltip>
                              :
                              <Tooltip title="Descargar resultados" placement="top-start" arrow>
                                <div>
                                  <button
                                    className="btn custom-btn-icons"
                                    onClick={()=>downloadFile()}
                                    type="button"
                                    disabled={isLoadingData || isErrorResponse || isLoadingDetailData|| saleOrder.length<1}
                                  >
                                    <i className="fas fa-download"></i>
                                  </button>
                                </div>
                              </Tooltip>
                        }
                          <Tooltip title="Filtrar" placement="top-start" arrow>
                            <button className="btn custom-btn-icons"
                              type="button" data-bs-toggle="collapse"
                              data-bs-target="#collapseExample" aria-expanded="false"
                              aria-controls="collapseExample">
                              <i className="fas fa-filter"></i>
                            </button>
                          </Tooltip>
                        </div>
                        <div className="collapse show" id="collapseExample">
                          <div className="filter-continer">
                            <div className="container mt-3">
                              <form className="row">
                                <div className="col-md-5 col-lg-4 mb-3">
                                  <TextField
                                    required
                                    select
                                    id="an8"
                                    name="an8"
                                    label="Num. cliente"
                                    variant="outlined"
                                    size="small"
                                    disabled={customers.isDisabled || customers.isLoading}
                                    fullWidth
                                    defaultValue={''}
                                    {...register('an8')}
                                    error={errors.an8 ? true : false}
                                  >
                                    <MenuItem value="">Seleccionar cliente</MenuItem>
                                    {customers.data.map(({ noCustomer, name }) => (
                                      <MenuItem key={noCustomer} value={noCustomer}>
                                        {noCustomer} - {name}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  <Typography variant="inherit" color="error">
                                    {errors.an8?.message}
                                </Typography>
                                </div>
                                <div className="col-md-5 col-lg-4 mb-3">
                                  <MuiPickersUtilsProvider
                                    utils={DateFnsUtils}
                                    locale={esLocale}>
                                    <DatePicker
                                      disableToolbar
                                      autoOk
                                      fullWidth
                                      required
                                      control={control}
                                      id="dateStart"
                                      name="dateStart"
                                      inputVariant="outlined"
                                      variant="inline"
                                      label="Fecha de inicio"
                                      size="small"
                                      format="dd/LLLL/yyyy"
                                      value={selectStartDate}
                                      onChange={handleStartDateChange}
                                    />
                                  </MuiPickersUtilsProvider>
                                </div>
                                <div className="col-md-5 col-lg-4 mb-3">
                                  <MuiPickersUtilsProvider
                                    utils={DateFnsUtils}
                                    locale={esLocale}>
                                    <DatePicker
                                      disableToolbar
                                      autoOk
                                      fullWidth
                                      required
                                      control={control}
                                      id="dateEnd"
                                      name="dateEnd"
                                      inputVariant="outlined"
                                      variant="inline"
                                      label="Fecha de Fin"
                                      size="small"
                                      format="dd/LLLL/yyyy"
                                      minDate={selectStartDate}
                                      minDateMessage="La fecha debe ser mayor a la de inicio"
                                      value={selectEndDate}
                                      onChange={handleEndDateChange}
                                    />
                                  </MuiPickersUtilsProvider>
                                </div>
                                <div className="col-md-5 col-lg-4 mb-3">
                                  <TextField
                                    className="w-100"
                                    id="referenceKey"
                                    name="referenceKey"
                                    label="Clave de referencia"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    {...register('referenceKey')}
                                  />
                                </div>
                                {/* <div className="col-md-5 col-lg-4 mb-3">
                                  <TextField
                                    className="w-100"
                                    id="invoiceNumber"
                                    name="invoiceNumber"
                                    label="Número de factura"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    {...register('invoiceNumber')}
                                  />
                                </div>
                                <div className="col-md-5 col-lg-4 mb-3">
                                  <TextField
                                    className="w-100"
                                    id="invoiceType"
                                    name="invoiceType"
                                    label="Tipo de factura"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    {...register('invoiceType')}
                                  />
                                </div>
                                <div className="col-md-5 col-lg-4 mb-3">
                                  <TextField
                                    className="w-100"
                                    id="origin"
                                    name="origin"
                                    label="Origen"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    {...register('origin')}
                                  />
                                </div> */}
                                <div className="col-12 w-100 d-flex justify-content-end">
                                  <Tooltip title={(!selectStartDate || !selectEndDate) ? "Completar filtros obligatorios" : "Buscar"} placement="top-start" arrow>
                                    <div>
                                      <button
                                        className="ms-2 mb-3 btn custom-btn"
                                        disabled={isLoadingData || !selectStartDate || !selectEndDate}
                                        onClick={handleSubmit(onSubmit)}
                                        type='submit'
                                        data-bs-target="#collapseExample"
                                        aria-controls="collapseExample"
                                      >
                                        <i className="fas fa-search"></i>
                                      </button>
                                    </div>
                                  </Tooltip>
                                </div>
                              </form>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        <div className='row pt-1 pb-1 d-flex justify-content-end'>
                          <div className="col-8">
                              <div className=" row">
                                  <div className="col-7 col-md-5">
                                      <span className="invoice-header-title">Total Remanente con IVA del período seleccionado:</span>
                                  </div>
                                  <div className="col-5 col-md-7">
                                      <span className="invoice-header-info">$ {saleOrder[0]?.totalRemainderWithVat > 0 ? parseFloat(saleOrder[0].totalRemainderWithVat).toLocaleString(currencySelected,{
                                                            minimumFractionDigits: 2
                                                        }) : '0.00'}</span>
                                  </div>
                              </div>
                          </div>
                        </div>
                        <div className='table-wrap'>
                          <table
                            className="table-order"
                            {...getTableProps()}
                          >
                            <thead>
                              {headerGroups.map((headerGroup, i) => (
                                <tr {...headerGroup.getHeaderGroupProps()} className="border-content sticky-header" key={i}>
                                  {headerGroup.headers.map((column, j) => (
                                    <th {...column.getHeaderProps({
                                      style: { minWidth: column.minWidth, width: column.width },
                                  })}
                                      className="border-element header-element header-text pt-3 pb-3" key={j}>
                                      {column.render("Header")}
                                    </th>
                                  ))}
                                </tr>
                              ))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                              {rows.map((row) => {
                                prepareRow(row);
                                return (
                                  <React.Fragment key={row.getRowProps().key}>
                                    <tr className="element-row border-content">
                                      {row.cells.map((cell, i) => {
                                        return (
                                          <td className="border-element content-text pt-2 pb-2"
                                            key={i}
                                            {...cell.getCellProps({
                                              style: {
                                                  minWidth: cell.column.minWidth,
                                                  width: cell.column.width,
                                                },
                                          })}>
                                            {
                                              cell.column.id === 'an8' ?
                                                getValues().an8
                                                : ((cell.column.Header === 'Importe con IVA' || cell.column.Header === 'Importe sin IVA' || cell.column.Header === 'Importe base' || cell.column.Header === 'IVA' || cell.column.Header === 'Saldo con IVA' || cell.column.Header === 'Remanente con IVA') ?
                                                (cell.value && cell.value !== '' && cell.value !== ' ' ?
                                                 `$ ${parseFloat(cell.value).toLocaleString(currencySelected, {
                                                      minimumFractionDigits: 2,
                                                      maximumFractionDigits: 2
                                                  })}`
                                                  : '') : cell.render("Cell"))
                                            }
                                          </td>
                                        );
                                      })}
                                    </tr>
                                    {row.isExpanded ?
                                      (
                                          <tr className="border-content-expanded">
                                          <td
                                          colSpan={visibleColumns.length}>
                                            <div className="row pb-2">
                                              <DetailTableAdvancesAndInvoices an8 = {getValues().an8} reference={row.original.referenceKey}  importWithVat={row.original.importWithVat} />
                                          </div>
                                          </td>
                                          </tr>
                                      ) : null}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                          {(isErrorResponse) &&
                            <div className="error-message border-content pt-4 pb-4">
                              <span className="">Error: {errorResponse && errorResponse.toString()}</span>
                            </div>}
                          {(isLoadingData) &&
                            <div className="border-content pt-4 pb-4 custom-icon-loading">
                              <i className="fas fa-spinner fa-spin"></i>
                            </div>}
                          {(saleOrder.length === 0 && !isLoadingData && !isErrorResponse && isSubmitingData) &&
                            <div className="element-row-message border-content pt-4 pb-4 ">
                              <span>Los filtros seleccionanos no devolvieron coincidencias.</span>
                            </div>}
                          {
                            (!isSubmitingData && !isLoadingData && !isErrorResponse) &&
                            <div className="element-row-message border-content pt-4 pb-4">
                              <span>Ejecute una búsqueda para obtener resultados.</span>
                            </div>
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
}

AdvanceAndInvoiceScreen.propTypes = {
  row: PropTypes.any,
  getToggleAllRowsSelectedProps: PropTypes.any,
  rows: PropTypes.any,
  toggleRowExpanded: PropTypes.any
};

export const DetailTableAdvancesAndInvoices = ({reference, importWithVat}) =>{

  const currencySelected = 'es-Mx';

  const dispatch = useDispatch();
  const history = useHistory();

  const tokenExpired = process.env.REACT_APP_TIME_EXPIRED_TOKEN;
  const sessionDifference = timeSessionDifference();

  const [saleOrderDetail, setSaleOrderDetail] = useState([]);
  const saleOrderDetailRef = useRef();

  const [isLoadingData, setIsLoadingData] = useState(false);

  const [errorResponse, setErrorResponse] = useState('');
  const [isErrorResponse, setIsErrorResponse] = useState(false);

  saleOrderDetailRef.current = saleOrderDetail;

  const columns = useMemo(
    () => [
      {
        Header: ()=> null,
        accessor: 'none',
        minWidth: 17,
        width: 17,
        maxWidth: 17
      },
      {
        Header: "Fecha Factura",
        accessor: "dateInvoice",
        minWidth: 50,
        width: 50,
        maxWidth: 50
      },
      {
        Header: "Referencia",
        accessor: "referenceKey",
        minWidth: 45,
        width: 45,
        maxWidth: 45
      },
      {
        Header: "Num. factura",
        accessor: "numberInvoice",
        minWidth: 50,
        width: 50,
        maxWidth: 50
      },
      {
        Header: 'Tipo factura',
        accessor: 'typeInvoice',
        minWidth: 35,
        width: 35,
        maxWidth: 35
      },
      {
        Header: 'Importe sin IVA',
        accessor: 'importWithoutVat',
        minWidth: 40,
        width: 40,
        maxWidth: 40
      },
      {
        Header: "IVA",
        accessor: "vat",
        minWidth: 40,
        width: 40,
        maxWidth: 40
      },
      {
        Header: 'Importe con IVA',
        accessor: 'importWithVat',
        minWidth: 40,
        width: 40,
        maxWidth: 40
      },
      {
        Header: "Remanente con IVA",
        accessor: "remainderWithVat",
        minWidth: 50,
        width: 50,
        maxWidth: 50
      },
      {
        Header: "Origen",
        accessor: "origin",
        minWidth: 40,
        width: 40,
        maxWidth: 40
      },
      {
        Header: "Descripción",
        accessor: "description",
        minWidth: 40,
        width: 40,
        maxWidth: 40
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable({
    columns,
    data: saleOrderDetail
  });

  useEffect(()=>{
    getSaleOrderDetail({reference, importWithVat});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);
 
  const getSaleOrderDetail = (ref) => {
    setIsLoadingData(true);
  
    console.log(ref)
    advanceAndInvoiceService.getAdvanceAndInvoiceDetail(ref.reference, ref.importWithVat)
      .then((response) => {
        if(response.message?.length > 1){
          setIsErrorResponse(true);
          setErrorResponse(response.message);
          setSaleOrderDetail([]);
        }else{
          setIsErrorResponse(false);
          const newResponseData = response?.data?.detailsOfManagementOfAdvancesAndInvoicesList ? response.data.detailsOfManagementOfAdvancesAndInvoicesList : [];
          setSaleOrderDetail(newResponseData);


        }
        setIsLoadingData(false);

      })
      .catch((error) => {
        setSaleOrderDetail({})
        if (error.response) {
          setErrorResponse(error.response.data.mensaje);
          if (sessionDifference > Number(tokenExpired)) {
            sesionExpired(dispatch, history);
          }
        } else {
          setErrorResponse(error);
        }
        setIsErrorResponse(true);
        actionErrorConnection(error, 'Error al recuperar información.');
      });

  }
  return(
    <>
    <div className='col-12'>
    <div className=''>
      <table
        className="table-order-detail"
          {...getTableProps()}
        >
          <thead>
            {headerGroups.map((headerGroup, i) => (
              <tr {...headerGroup.getHeaderGroupProps()} className="border-content sticky-header" key={i}>
                {headerGroup.headers.map((column, j) => (
                  <th {...column.getHeaderProps({
                    style: { minWidth: column.minWidth, width: column.width, maxWidth: column.maxWidth },
                })}
                    className="border-element header-element header-text pt-3 pb-3" key={j}>
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <React.Fragment key={row.getRowProps().key}>
                  <tr className="element-row border-content">
                    {row.cells.map((cell, i) => {
                      return (
                        <td className="border-element content-text pt-2 pb-2"
                          key={i}
                          {...cell.getCellProps({
                            style: {
                                minWidth: cell.column.minWidth,
                                width: cell.column.width,
                                maxWidth: cell.column.maxWidth
                              },
                        })}>
                          {console.log(cell.column)}
                          {

                            (cell.column.Header === 'Importe con IVA' || cell.column.Header === 'Importe sin IVA' || cell.column.Header === 'Importe base' || cell.column.Header === 'IVA' || cell.column.Header === 'Saldo con IVA' || cell.column.Header === 'Remanente con IVA') ?
                              (cell.value && cell.value !== '' && cell.value !== ' ' ?
                                `$ ${parseFloat(cell.value).toLocaleString(currencySelected, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })}`
                                : '') : (cell.column.id === 'remainderWithVatTitle' ? <span className="fw-bold">{cell.value}</span> : cell.render("Cell"))
                          }
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {(isErrorResponse) &&
          <div className="error-message border-content pt-4 pb-4">
            <span className="">Error: {errorResponse && errorResponse.toString()}</span>
          </div>}
        {(isLoadingData) &&
          <div className="border-content pt-4 pb-4 custom-icon-loading">
            <i className="fas fa-spinner fa-spin"></i>
          </div>}
        {(saleOrderDetail.length === 0 && !isLoadingData && !isErrorResponse) &&
          <div className="element-row-message border-content pt-4 pb-4 ">
            <span>No se devolvieron coincidencias relacionadas a la referencia.</span>
          </div>}

          {/* <div className='d-flex justify-content-end pt-3 pb-3'>
            <div className="col-md-4">
                <div className=" row">
                    <div className="col-6 col-md-5">
                        <span className="invoice-footer-detail-title">Total Remanente con IVA:</span>
                    </div>
                    <div className="col-6 col-md-7">
                        <span className="invoice-footer-detail-info">$ {saleOrderDetail.totalRemainderWithVat && saleOrderDetail.totalRemainderWithVat !== 0 ? parseFloat(saleOrderDetail.totalRemainderWithVat).toLocaleString(currencySelected,{
                            minimumFractionDigits: 2
                        }) : '0.00'}</span>
                    </div>
                </div>
            </div>
          </div> */}
      </div>
    </div>
    </>
  )

}

DetailTableAdvancesAndInvoices.propTypes = {
  row: PropTypes.any,
  getToggleAllRowsSelectedProps: PropTypes.any,
  rows: PropTypes.any,
  reference: PropTypes.string,
  importWithVat: PropTypes.string,
  setSaleOrderDetailParent:PropTypes.any, 
  setIsLoadingDetailData:PropTypes.any
};
